import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Box, TextField, RadioGroup, FormControlLabel, Radio, Slider, SvgIcon, Button, ButtonGroup, Switch } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VibrationIcon from '@mui/icons-material/Vibration';
import StarIcon from '@mui/icons-material/Star';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import ratingService from '../../services/RatingService';
import { useTranslation } from 'react-i18next';
import { getAnimationSpeed } from '../../config/animationConfig';
import { InAppBrowser } from '@capacitor/inappbrowser';
import { Capacitor } from '@capacitor/core';
import soundVibrationService from '../../services/SoundVibrationService';
// Version is read from .env (REACT_APP_VERSION)

// Instagram Icon
const InstagramIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 24 24">
        <path fill="currentColor" d="M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.8,5.8 0 0,1 16.2,22H7.8C4.6,22 2,19.4 2,16.2V7.8A5.8,5.8 0 0,1 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z" />
    </SvgIcon>
);

// X (Twitter) Icon
const XIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 24 24">
        <path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </SvgIcon>
);

// Facebook Icon
const FacebookIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 24 24">
        <path fill="currentColor" d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z" />
    </SvgIcon>
);

// Function to open URLs in external browser
const openExternalUrl = async (url) => {
    if (Capacitor.isNativePlatform()) {
        try {
            await InAppBrowser.openInExternalBrowser({ url });
        } catch (error) {
            console.error('Error opening external URL:', error);
            window.open(url, '_blank');
        }
    } else {
        window.open(url, '_blank');
    }
};

const SettingsModal = ({ open, onClose }) => {
    const { t } = useTranslation();
    const [tableColor, setTableColor] = useState('#257241');
    const [cardBack, setCardBack] = useState('001');
    const [animationSpeed, setAnimationSpeed] = useState(1.2);
    const [difficulty, setDifficulty] = useState('NORMAL');
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [vibrationEnabled, setVibrationEnabled] = useState(true);

    // Load settings from localStorage on mount
    useEffect(() => {
        const savedColor = localStorage.getItem('tableColor');
        if (savedColor) {
            setTableColor(savedColor);
        }

        const savedCardBack = localStorage.getItem('cardBack');
        if (savedCardBack) {
            setCardBack(savedCardBack);
        }

        // Load animation speed
        const savedSpeed = getAnimationSpeed();
        setAnimationSpeed(savedSpeed);

        const savedDifficulty = localStorage.getItem('difficulty');
        if (savedDifficulty) {
            setDifficulty(savedDifficulty);
        }

        // Load sound and vibration settings
        const savedSoundEnabled = localStorage.getItem('soundEnabled');
        setSoundEnabled(savedSoundEnabled === null ? true : savedSoundEnabled === 'true');

        const savedVibrationEnabled = localStorage.getItem('vibrationEnabled');
        setVibrationEnabled(savedVibrationEnabled === null ? true : savedVibrationEnabled === 'true');
    }, []);

    // Sync settings when changed externally (e.g. from GameSetup)
    useEffect(() => {
        const onDifficultyChanged = (e) => setDifficulty(e.detail);
        const onTableColorChanged = (e) => setTableColor(e.detail);
        const onCardBackChanged = (e) => setCardBack(e.detail);
        const onAnimationSpeedChanged = (e) => setAnimationSpeed(e.detail);

        window.addEventListener('difficultyChanged', onDifficultyChanged);
        window.addEventListener('tableColorChanged', onTableColorChanged);
        window.addEventListener('cardBackChanged', onCardBackChanged);
        window.addEventListener('animationSpeedChanged', onAnimationSpeedChanged);

        return () => {
            window.removeEventListener('difficultyChanged', onDifficultyChanged);
            window.removeEventListener('tableColorChanged', onTableColorChanged);
            window.removeEventListener('cardBackChanged', onCardBackChanged);
            window.removeEventListener('animationSpeedChanged', onAnimationSpeedChanged);
        };
    }, []);

    // Save difficulty to localStorage when it changes
    const handleDifficultyChange = (e) => {
        const newDifficulty = e.target.value;
        setDifficulty(newDifficulty);
        localStorage.setItem('difficulty', newDifficulty);
        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('difficultyChanged', { detail: newDifficulty }));
    };

    // Save table color to localStorage when it changes
    const handleColorChange = (e) => {
        const newColor = e.target.value;
        setTableColor(newColor);
        localStorage.setItem('tableColor', newColor);
        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('tableColorChanged', { detail: newColor }));
    };

    // Save card back to localStorage when it changes
    const handleCardBackChange = (e) => {
        const newCardBack = e.target.value;
        setCardBack(newCardBack);
        localStorage.setItem('cardBack', newCardBack);
        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('cardBackChanged', { detail: newCardBack }));
    };

    // Save animation speed to localStorage when it changes
    const handleAnimationSpeedChange = (event, newValue) => {
        setAnimationSpeed(newValue);
        localStorage.setItem('animationSpeed', newValue.toString());
        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('animationSpeedChanged', { detail: newValue }));
    };

    // Toggle sound enabled/disabled
    const handleSoundToggle = (event) => {
        const enabled = event.target.checked;
        setSoundEnabled(enabled);
        soundVibrationService.setSoundEnabled(enabled);
    };

    // Toggle vibration enabled/disabled
    const handleVibrationToggle = (event) => {
        const enabled = event.target.checked;
        setVibrationEnabled(enabled);
        soundVibrationService.setVibrationEnabled(enabled);
    };

    // Marks for the slider (left=slow, right=fast like YouTube)
    const speedMarks = [
        { value: 0.2, label: t('settings.animation_speed_slow') },
        { value: 1.0, label: t('settings.animation_speed_normal') },
        { value: 3.0, label: t('settings.animation_speed_fast') },
    ];

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="sm" 
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                }
            }}
        >
            <DialogTitle>
                {t('settings.title')}
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        {t('settings.language')}
                    </Typography>
                    <LanguageSelector />
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        {t('settings.difficulty')}
                    </Typography>
                    <ButtonGroup variant="contained" color="primary" fullWidth sx={{ mb: 1 }}>
                        <Button
                            onClick={() => handleDifficultyChange({ target: { value: 'EASY' } })}
                            variant={difficulty === 'EASY' ? 'contained' : 'outlined'}
                            color={difficulty === 'EASY' ? 'success' : 'primary'}
                        >
                            🟢 {t('settings.difficulty_easy')}
                        </Button>
                        <Button
                            onClick={() => handleDifficultyChange({ target: { value: 'NORMAL' } })}
                            variant={difficulty === 'NORMAL' ? 'contained' : 'outlined'}
                            color={difficulty === 'NORMAL' ? 'warning' : 'primary'}
                        >
                            🟡 {t('settings.difficulty_normal')}
                        </Button>
                        <Button
                            onClick={() => handleDifficultyChange({ target: { value: 'HARD' } })}
                            variant={difficulty === 'HARD' ? 'contained' : 'outlined'}
                            color={difficulty === 'HARD' ? 'error' : 'primary'}
                        >
                            🔴 {t('settings.difficulty_hard')}
                        </Button>
                    </ButtonGroup>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        {t('settings.table_color')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                            type="color"
                            value={tableColor}
                            onChange={handleColorChange}
                            sx={{ width: 100 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            {tableColor}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        {t('settings.card_back')}
                    </Typography>
                    <RadioGroup value={cardBack} onChange={handleCardBackChange}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {['001', '002', '003'].map((back) => (
                                <FormControlLabel
                                    key={back}
                                    value={back}
                                    control={<Radio />}
                                    label={
                                        <Box sx={{ textAlign: 'center' }}>
                                            <img
                                                src={`assets/images/deck/${back}.png`}
                                                alt={`${t('settings.card_back')} ${back}`}
                                                style={{
                                                    width: 60,
                                                    height: 90,
                                                    objectFit: 'cover',
                                                    border: cardBack === back ? '3px solid #1976d2' : '1px solid #ccc',
                                                    borderRadius: 4,
                                                    display: 'block',
                                                    marginBottom: 4
                                                }}
                                            />
                                            <Typography variant="caption">{back}</Typography>
                                        </Box>
                                    }
                                    sx={{
                                        margin: 0,
                                        '& .MuiFormControlLabel-label': {
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center'
                                        }
                                    }}
                                />
                            ))}
                        </Box>
                    </RadioGroup>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        {t('settings.animation_speed')}
                    </Typography>
                    <Box sx={{ px: 2 }}>
                        <Slider
                            value={animationSpeed}
                            onChange={handleAnimationSpeedChange}
                            min={0.2}
                            max={3}
                            step={0.1}
                            marks={speedMarks}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => `${value.toFixed(1)}x`}
                            sx={{
                                '& .MuiSlider-markLabel': {
                                    fontSize: '0.75rem',
                                },
                            }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                            {animationSpeed.toFixed(1)}x
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        {t('settings.sound_vibration')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {soundEnabled ? <VolumeUpIcon color="primary" /> : <VolumeOffIcon color="disabled" />}
                                <Typography variant="body1">{t('settings.sound')}</Typography>
                            </Box>
                            <Switch
                                checked={soundEnabled}
                                onChange={handleSoundToggle}
                                color="primary"
                            />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <VibrationIcon color={vibrationEnabled ? 'primary' : 'disabled'} />
                                <Typography variant="body1">{t('settings.vibration')}</Typography>
                            </Box>
                            <Switch
                                checked={vibrationEnabled}
                                onChange={handleVibrationToggle}
                                color="primary"
                            />
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        {t('settings.developer')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 1 }}>
                        {/* Instagram Button */}
                        <IconButton
                            onClick={() => openExternalUrl('https://instagram.com/carlosgutrod')}
                            sx={{
                                background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                                color: 'white',
                                width: 48,
                                height: 48,
                                '&:hover': {
                                    background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                                    opacity: 0.8,
                                    transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease-in-out',
                            }}
                        >
                            <InstagramIcon />
                        </IconButton>
                        {/* X (Twitter) Button */}
                        <IconButton
                            onClick={() => openExternalUrl('https://x.com/carlosgutrod')}
                            sx={{
                                background: '#000000',
                                color: 'white',
                                width: 48,
                                height: 48,
                                '&:hover': {
                                    background: '#333333',
                                    transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease-in-out',
                            }}
                        >
                            <XIcon />
                        </IconButton>
                        {/* Facebook Button */}
                        <IconButton
                            onClick={() => openExternalUrl('https://www.facebook.com/carlosgutrod/')}
                            sx={{
                                background: '#1877F2',
                                color: 'white',
                                width: 48,
                                height: 48,
                                '&:hover': {
                                    background: '#166FE5',
                                    transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease-in-out',
                            }}
                        >
                            <FacebookIcon />
                        </IconButton>
                        {/* Rate App Button */}
                        <IconButton
                            onClick={() => ratingService.openStoreDirectly()}
                            title={t('settings.rate_app')}
                            sx={{
                                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                                color: '#1a1a1a',
                                width: 48,
                                height: 48,
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #FFE44D 0%, #FFB733 100%)',
                                    transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease-in-out',
                            }}
                        >
                            <StarIcon />
                        </IconButton>
                    </Box>
                </Box>
                <Box>
                    <Typography variant="subtitle1" gutterBottom>
                        {t('settings.version')}
                    </Typography>
                    <Typography variant="body2">{process.env.REACT_APP_VERSION || '1.0.0'}</Typography>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default SettingsModal;
